import { Customer, CustomerDeadlineView, CustomerMonthlyStatus, CustomerIndividualDeadline, SnoozedDeadlineInfo, LohnscheinNachzuegler, LohnscheinLaufFertigmeldungen, SpezialfristNachzuegler, SpezialfristFertigmeldungen, DatenAnlieferungInfo, LohnscheinRelativBezug, IndividualNachzuegler } from '../types';
import { type LohnscheinVersandLaufConfig } from './dateUtils';

/**
 * SNOOZE FEATURE: Prüft ob eine Deadline aktuell zurückgestellt (gesnoozed) ist.
 * Zurückgestellt bedeutet: heute liegt VOR dem snoozed_until Datum.
 */
export const isDeadlineSnoozed = (
  fieldName: string,
  status: CustomerMonthlyStatus | null
): boolean => {
  if (!status?.snoozed_deadlines) return false;
  const snoozeInfo = status.snoozed_deadlines[fieldName];
  if (!snoozeInfo) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const snoozedUntil = new Date(snoozeInfo.snoozed_until + 'T00:00:00');
  return today < snoozedUntil;
};

/**
 * SNOOZE FEATURE: Gibt die Snooze-Info für eine Deadline zurück (oder null).
 */
export const getSnoozedInfo = (
  fieldName: string,
  status: CustomerMonthlyStatus | null
): SnoozedDeadlineInfo | null => {
  if (!status?.snoozed_deadlines) return null;
  return status.snoozed_deadlines[fieldName] || null;
};

/**
 * Prädikate "Feature aktiv?" für die drei Spezialfristen. Eine Frist gilt als
 * aktiv, wenn entweder ein fester Tag ODER ein Lohnlauf-Bezug (relative Frist)
 * konfiguriert ist. Solange keine relative Frist existiert (bezug_lohnlauf NULL),
 * verhalten sich die Prädikate exakt wie die frühere reine Tag-Prüfung.
 */
export const hasLohnscheinVersand = (customer: Customer): boolean =>
  !!(customer.lohnschein_versand_tag || customer.lohnschein_versand_bezug_lohnlauf);

export const hasOppmQrVersand = (customer: Customer): boolean =>
  !!(customer.oppm_qr_versand_tag || customer.oppm_qr_versand_bezug_lohnlauf);

export const hasMonatsuebersichtQrVersand = (customer: Customer): boolean =>
  !!(customer.monatsuebersicht_qr_versand_tag || customer.monatsuebersicht_qr_versand_bezug_lohnlauf);

/**
 * OFFEN NACH FINALISIERUNG: Bleibt eine individuelle Frist im angegebenen
 * Arbeitsmonat bei der Finalisierung offen?
 *
 * Das gilt nur für wiederkehrende Fristen mit gesetztem
 * offen_nach_finalisierung_ab und nur für Arbeitsmonate ab diesem Monat.
 * Für alle anderen Fristen bleibt es beim bisherigen Verhalten: Die
 * Finalisierung hakt sie automatisch ab, und es entsteht kein Nachzügler.
 *
 * Eine Quelle für beide Stellen, an denen die Regel greift: das Überspringen
 * beim automatischen Abhaken in der Finalisierung und die Bildung der
 * Nachzügler-Kacheln. So können die beiden nicht auseinanderlaufen.
 *
 * @param deadline - die individuelle Frist
 * @param year - Arbeitsjahr
 * @param month - Arbeitsmonat 1-12
 */
export const istOffenNachFinalisierung = (
  deadline: CustomerIndividualDeadline,
  year: number,
  month: number
): boolean => {
  if (!deadline.is_recurring || !deadline.offen_nach_finalisierung_ab) return false;
  const [abJahr, abMonat] = deadline.offen_nach_finalisierung_ab.split('-').map(Number);
  if (!abJahr || !abMonat) return false;
  return year * 12 + month >= abJahr * 12 + abMonat;
};

/**
 * WARTET AUF KUNDEN: Liegt die Abrechnung laut Anlieferungs-Markierung gerade
 * beim Kunden? Das ist der Fall, wenn "An Kunden geliefert" gesetzt ist und
 * danach noch keine Rückmeldung markiert wurde. Der Vergleich der Zeitstempel
 * erlaubt mehrere Runden (geliefert, Rückmeldung, erneut geliefert).
 */
export const istWartetAufKunden = (info: DatenAnlieferungInfo | null | undefined): boolean => {
  if (!info?.beim_kunden_seit || !info.beim_kunden_at) return false;
  if (!info.rueckmeldung_at) return true;
  return info.beim_kunden_at > info.rueckmeldung_at;
};

/**
 * WARTET AUF KUNDEN: Ruht die Frist eines Lohnlaufs gerade beim Kunden?
 * Voraussetzung: Lohnlauf konfiguriert, "Frist läuft ab Datenanlieferung"
 * aktiv, Lohnlauf noch nicht erledigt und die Markierung im Zustand
 * "beim Kunden".
 */
export const lohnlaufWartetAufKunden = (
  customer: Customer,
  status: CustomerMonthlyStatus | null,
  lohnlauf: 1 | 2
): boolean => {
  if (!status) return false;
  const tag = lohnlauf === 1 ? customer.erster_lohnlauf_tag : customer.zweiter_lohnlauf_tag;
  const fristAbAnlieferung = lohnlauf === 1
    ? customer.erster_lohnlauf_frist_ab_anlieferung
    : customer.zweiter_lohnlauf_frist_ab_anlieferung;
  if (!tag || !fristAbAnlieferung) return false;
  const fieldName = lohnlauf === 1 ? 'lohnlauf_1_completed' : 'lohnlauf_2_completed';
  if (status[fieldName]) return false;
  return istWartetAufKunden(status.daten_anlieferungen?.[fieldName]);
};

/**
 * WARTET AUF KUNDEN: Welche Lohnschein-Läufe sind relativ zu ihrem Lohnlauf
 * konfiguriert (bezug_werktage_offset gesetzt)? Nur solche Läufe ankern am
 * Lohnlauf und ruhen mit ihm. Fest datierte Läufe bleiben unberührt.
 */
export const lohnscheinRelativAus = (configs: LohnscheinVersandLaufConfig[] | undefined): LohnscheinRelativBezug => {
  const istRelativ = (lohnlauf: 1 | 2) => (configs || []).some(c =>
    c.lohnlauf === lohnlauf && c.bezug_werktage_offset !== null && c.bezug_werktage_offset !== undefined
  );
  return { lauf1: istRelativ(1), lauf2: istRelativ(2) };
};

/**
 * WARTET AUF KUNDEN: Feldnamen aller Fristen, die gerade beim Kunden ruhen.
 *
 * Das ist der Lohnlauf selbst und jede Frist, die relativ zu genau diesem
 * Lohnlauf konfiguriert ist: OPPM QR-Code-Versand, OPPM Monatsübersicht-
 * Versand, relativ konfigurierte Lohnschein-Läufe und wiederkehrende
 * individuelle Fristen mit Lohnlauf-Bezug. Ruhende Fristen werden nie als
 * überfällig gewertet; der Kunde bleibt aber in der Gruppe "Sofortiger
 * Handlungsbedarf" (siehe calculateCustomerPriority).
 *
 * Nachzügler gehören zu abgeschlossenen Monaten, tragen eigene Schlüssel und
 * ruhen deshalb nie. Ob eine ruhende Frist selbst schon erledigt ist, prüfen
 * die Aufrufer wie bisher über ihr eigenes Erledigt-Kennzeichen.
 */
export const ermittleRuhendeFristen = (
  customer: Customer,
  status: CustomerMonthlyStatus | null,
  individualDeadlines?: CustomerIndividualDeadline[],
  lohnscheinRelativ?: LohnscheinRelativBezug
): Set<string> => {
  const ruhend = new Set<string>();
  ([1, 2] as const).forEach(lohnlauf => {
    if (!lohnlaufWartetAufKunden(customer, status, lohnlauf)) return;
    ruhend.add(lohnlauf === 1 ? 'lohnlauf_1_completed' : 'lohnlauf_2_completed');
    if (customer.oppm_qr_versand_bezug_lohnlauf === lohnlauf) {
      ruhend.add('oppm_qr_versand_completed');
    }
    if (customer.monatsuebersicht_qr_versand_bezug_lohnlauf === lohnlauf) {
      ruhend.add('monatsuebersicht_qr_versand_completed');
    }
    if (lohnlauf === 1 ? lohnscheinRelativ?.lauf1 : lohnscheinRelativ?.lauf2) {
      ruhend.add(lohnlauf === 1 ? 'lohnschein_versand_1_completed' : 'lohnschein_versand_2_completed');
    }
    (individualDeadlines || []).forEach(ind => {
      if (ind.is_recurring && ind.bezug_lohnlauf === lohnlauf) {
        ruhend.add(`individual_deadline_${ind.id}`);
      }
    });
  });
  return ruhend;
};

/**
 * NACHZÜGLER: Eindeutiger Feldname einer Nachzügler-Kachel.
 *
 * Nachzügler gehören zu einem bereits abgeschlossenen Monat und haben deshalb
 * KEINEN Eintrag in snoozed_deadlines des aktuellen Monatsstatus. Der Name ist
 * damit garantiert snooze-frei, was gewollt ist: eine Nachzügler-Frist lässt
 * sich nicht zurückstellen, sondern nur beauftragen oder bewusst abhaken.
 */
export const getNachzueglerKey = (nachzuegler: LohnscheinNachzuegler): string =>
  `nachzuegler_lohnschein_${nachzuegler.arbeitsjahr}_${nachzuegler.arbeitsmonat}_${nachzuegler.lohnlauf}`;

/**
 * NACHZÜGLER SPEZIALFRISTEN: Eindeutiger Feldname einer Nachzügler-Kachel.
 *
 * Gleiche Überlegung wie bei getNachzueglerKey: Der Name gehört zu einem
 * bereits abgeschlossenen Monat und hat deshalb garantiert keinen Eintrag in
 * snoozed_deadlines des aktuellen Monatsstatus. Damit ist die Kachel
 * snooze-frei, was gewollt ist.
 */
export const getSpezialfristNachzueglerKey = (nachzuegler: SpezialfristNachzuegler): string =>
  `nachzuegler_${nachzuegler.frist_art}_${nachzuegler.arbeitsjahr}_${nachzuegler.arbeitsmonat}`;

/**
 * NACHZÜGLER INDIVIDUELLE FRISTEN: Eindeutiger Feldname einer Nachzügler-Kachel.
 *
 * Gleiche Überlegung wie bei getNachzueglerKey: snooze-frei, weil der Name zu
 * einem abgeschlossenen Monat gehört. Der Name unterscheidet sich bewusst vom
 * Feldnamen der regulären Kachel derselben Frist (individual_deadline_<id>),
 * damit Ladezustand, Ruhen und Zurückstellen der regulären Kachel nicht auf die
 * Nachzügler-Kachel übergreifen. Er passt trotzdem in das Muster
 * individual_deadline_* von StatusField.
 */
export const getIndividualNachzueglerKey = (nachzuegler: IndividualNachzuegler): string =>
  `individual_deadline_nachzuegler_${nachzuegler.deadline_id}_${nachzuegler.arbeitsjahr}_${nachzuegler.arbeitsmonat}`;

/**
 * Berechnet ob ein Kunde aktuell überfällige Deadlines hat
 * Erweitert um individuelle Deadlines und Snooze-Feature
 *
 * NACHZÜGLER (optionaler 5. Parameter): Noch offene Lohnschein-Läufe des zuletzt
 * abgeschlossenen Monats zählen mit, damit ein Kunde mit vergessenem Versand
 * nicht als "unter Kontrolle" durchrutscht. Bestehende Aufrufe mit vier
 * Argumenten verhalten sich unverändert.
 *
 * LOHNSCHEIN-FERTIGMELDUNGEN (optionaler 6. Parameter): Die Erledigung je
 * Lohnlauf stammt aus lohnschein_versand_lauf_status und liegt an der View, nicht
 * im Monatsstatus. Ohne diesen Parameter gelten beide Läufe als nicht erledigt.
 *
 * SPEZIALFRISTEN (optionale 7. und 8. Parameter): Die Erledigung der beiden
 * QR-Fristen stammt aus customer_periodenfrist_status und hängt an der
 * Abrechnungsperiode, nicht am Arbeitsmonat. Der 8. Parameter trägt die noch
 * offenen QR-Fristen des zuletzt abgeschlossenen Monats nach. Ohne diese
 * Parameter gelten die Fristen als nicht erledigt und es gibt keine Nachzügler.
 *
 * WARTET AUF KUNDEN (optionaler 9. Parameter): Fristen, die gerade beim Kunden
 * ruhen, sind nie überfällig (siehe ermittleRuhendeFristen). Der Parameter
 * sagt, welche Lohnschein-Läufe relativ konfiguriert sind; ohne ihn ruhen
 * Lohnschein-Läufe nicht, alle übrigen ruhenden Fristen schon.
 *
 * NACHZÜGLER INDIVIDUELLE FRISTEN (optionaler 10. Parameter): noch offene
 * individuelle Fristen des zuletzt abgeschlossenen Monats (nur Fristen mit
 * offen_nach_finalisierung_ab). Ohne diesen Parameter gibt es keine.
 */
export const calculateIsOverdue = (
  deadlines: CustomerDeadlineView['deadlines'],
  status: CustomerMonthlyStatus | null,
  customer: Customer,
  individualDeadlines?: CustomerIndividualDeadline[],
  nachzuegler?: LohnscheinNachzuegler[],
  lohnscheinCompleted?: LohnscheinLaufFertigmeldungen,
  spezialfristCompleted?: SpezialfristFertigmeldungen,
  spezialfristNachzuegler?: SpezialfristNachzuegler[],
  lohnscheinRelativ?: LohnscheinRelativBezug,
  individualNachzuegler?: IndividualNachzuegler[]
): boolean => {
  const now = new Date();
  const ruhendeFristen = ermittleRuhendeFristen(customer, status, individualDeadlines, lohnscheinRelativ);
  
  // Standard-Deadlines prüfen (mit fieldName für Snooze-Check)
  const uncompleted: Array<{ date: Date | undefined; completed: boolean | undefined; fieldName: string }> = [
    { date: deadlines.lohnlauf_1, completed: status?.lohnlauf_1_completed, fieldName: 'lohnlauf_1_completed' },
    { date: deadlines.lohnlauf_2, completed: status?.lohnlauf_2_completed, fieldName: 'lohnlauf_2_completed' },
    { date: deadlines.lohnsteuer, completed: status?.lohnsteuer_completed, fieldName: 'lohnsteuer_completed' },
    // SV-Beiträge nur prüfen wenn sv_pflicht = true
    ...(customer.sv_pflicht ? [{ date: deadlines.sv_beitraege, completed: status?.sv_beitraege_completed, fieldName: 'sv_beitraege_completed' }] : []),
    // Lohnschein-Versand (Zwei-Lauf-Modell): je Lauf ein Eintrag, gegatet auf die Kachel.
    // Erledigung kommt aus den Lauf-Fertigmeldungen, nicht aus dem Monatsstatus.
    ...(deadlines.lohnschein_versand_1 ? [{ date: deadlines.lohnschein_versand_1, completed: lohnscheinCompleted?.lauf1, fieldName: 'lohnschein_versand_1_completed' }] : []),
    ...(deadlines.lohnschein_versand_2 ? [{ date: deadlines.lohnschein_versand_2, completed: lohnscheinCompleted?.lauf2, fieldName: 'lohnschein_versand_2_completed' }] : []),
    // OPPM QR-Code-Versand nur prüfen wenn am Standort konfiguriert.
    // Erledigung kommt aus customer_periodenfrist_status (periodenbezogen),
    // nicht mehr aus dem Monatsstatus des Arbeitsmonats.
    ...(hasOppmQrVersand(customer) ? [{ date: deadlines.oppm_qr_versand, completed: spezialfristCompleted?.oppmQr, fieldName: 'oppm_qr_versand_completed' }] : []),
    // OPPM Monatsübersicht QR-Code-Versand nur prüfen wenn am Standort konfiguriert
    ...(hasMonatsuebersichtQrVersand(customer) ? [{ date: deadlines.monatsuebersicht_qr_versand, completed: spezialfristCompleted?.monatsuebersichtQr, fieldName: 'monatsuebersicht_qr_versand_completed' }] : [])
  ];
  
  // NACHZÜGLER: offene Lohnschein-Läufe des zuletzt abgeschlossenen Monats.
  // Sie sind per Definition unerledigt (erledigte werden gar nicht erst erzeugt).
  if (nachzuegler && nachzuegler.length > 0) {
    nachzuegler.forEach(n => {
      uncompleted.push({
        date: n.faelligkeit,
        completed: false,
        fieldName: getNachzueglerKey(n)
      });
    });
  }

  // NACHZÜGLER SPEZIALFRISTEN: offene QR-Fristen des zuletzt abgeschlossenen
  // Monats. Ebenfalls per Definition unerledigt.
  if (spezialfristNachzuegler && spezialfristNachzuegler.length > 0) {
    spezialfristNachzuegler.forEach(n => {
      uncompleted.push({
        date: n.faelligkeit,
        completed: false,
        fieldName: getSpezialfristNachzueglerKey(n)
      });
    });
  }

  // NACHZÜGLER INDIVIDUELLE FRISTEN: offene individuelle Fristen des zuletzt
  // abgeschlossenen Monats. Ebenfalls per Definition unerledigt.
  if (individualNachzuegler && individualNachzuegler.length > 0) {
    individualNachzuegler.forEach(n => {
      uncompleted.push({
        date: n.faelligkeit,
        completed: false,
        fieldName: getIndividualNachzueglerKey(n)
      });
    });
  }
  
  // Individuelle Deadlines hinzufügen
  if (individualDeadlines && individualDeadlines.length > 0) {
    individualDeadlines.forEach(individualDeadline => {
      const deadlineKey = `individual_${individualDeadline.id}`;
      const deadlineDate = deadlines[deadlineKey];
      const isCompleted = status?.individual_deadlines_status?.[individualDeadline.id] || false;
      
      uncompleted.push({
        date: deadlineDate,
        completed: isCompleted,
        fieldName: `individual_deadline_${individualDeadline.id}`
      });
    });
  }
  
  return uncompleted.some(({ date, completed, fieldName }) => 
    !completed && date && date < now && !isDeadlineSnoozed(fieldName, status) && !ruhendeFristen.has(fieldName)
  );
};

/**
 * Hilfsfunktion: Berechnet den Sonntag der aktuellen Woche
 */
const getSundayOfCurrentWeek = (date: Date): Date => {
  const result = new Date(date);
  const day = result.getDay();
  const diff = day === 0 ? 0 : 7 - day; // Wenn heute Sonntag ist, dann heute, sonst nächster Sonntag
  result.setDate(result.getDate() + diff);
  result.setHours(23, 59, 59, 999);
  return result;
};

/**
 * Hilfsfunktion: Berechnet den Sonntag der nächsten Woche
 */
const getSundayOfNextWeek = (date: Date): Date => {
  const sundayThisWeek = getSundayOfCurrentWeek(date);
  const result = new Date(sundayThisWeek);
  result.setDate(result.getDate() + 7);
  return result;
};

/**
 * Berechnet die Priorität eines Kunden basierend auf seinen Deadlines
 * Erweitert um individuelle Deadlines, präzise Wochen-Logik und Snooze-Feature
 *
 * NACHZÜGLER (optionaler 5. Parameter): Ein offener Lohnschein-Lauf aus dem
 * zuletzt abgeschlossenen Monat ist in aller Regel überfällig und hebt den
 * Kunden damit in die Gruppe "Sofortiger Handlungsbedarf". Bestehende Aufrufe
 * mit vier Argumenten verhalten sich unverändert.
 *
 * LOHNSCHEIN-FERTIGMELDUNGEN (optionaler 6. Parameter): siehe calculateIsOverdue.
 *
 * SPEZIALFRISTEN (optionale 7. und 8. Parameter): siehe calculateIsOverdue.
 *
 * WARTET AUF KUNDEN (optionaler 9. Parameter): siehe calculateIsOverdue. Eine
 * ruhende Frist zählt unabhängig von ihrem Datum als Priorität 2. Der Kunde
 * bleibt damit in der Gruppe "Sofortiger Handlungsbedarf", ohne als überfällig
 * zu gelten; dort sortiert ihn das Fristenboard hinter die Kunden mit echtem
 * Handlungsbedarf.
 *
 * NACHZÜGLER INDIVIDUELLE FRISTEN (optionaler 10. Parameter): siehe
 * calculateIsOverdue.
 */
export const calculateCustomerPriority = (
  deadlines: CustomerDeadlineView['deadlines'],
  status: CustomerMonthlyStatus | null,
  customer: Customer,
  individualDeadlines?: CustomerIndividualDeadline[],
  nachzuegler?: LohnscheinNachzuegler[],
  lohnscheinCompleted?: LohnscheinLaufFertigmeldungen,
  spezialfristCompleted?: SpezialfristFertigmeldungen,
  spezialfristNachzuegler?: SpezialfristNachzuegler[],
  lohnscheinRelativ?: LohnscheinRelativBezug,
  individualNachzuegler?: IndividualNachzuegler[]
): number => {
  const now = new Date();
  const ruhendeFristen = ermittleRuhendeFristen(customer, status, individualDeadlines, lohnscheinRelativ);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const sundayThisWeek = getSundayOfCurrentWeek(today);
  const sundayNextWeek = getSundayOfNextWeek(today);
  
  let highestPriority = 999; // Niedrigste Priorität als Start
  
  // Standard-Deadlines prüfen (mit fieldName für Snooze-Check)
  const deadlinesToCheck: Array<{ date: Date | undefined; completed: boolean | undefined; fieldName: string }> = [
    { date: deadlines.lohnlauf_1, completed: status?.lohnlauf_1_completed, fieldName: 'lohnlauf_1_completed' },
    { date: deadlines.lohnlauf_2, completed: status?.lohnlauf_2_completed, fieldName: 'lohnlauf_2_completed' },
    { date: deadlines.lohnsteuer, completed: status?.lohnsteuer_completed, fieldName: 'lohnsteuer_completed' },
    // SV-Beiträge nur prüfen wenn sv_pflicht = true
    ...(customer.sv_pflicht ? [{ date: deadlines.sv_beitraege, completed: status?.sv_beitraege_completed, fieldName: 'sv_beitraege_completed' }] : []),
    // Lohnschein-Versand (Zwei-Lauf-Modell): je Lauf ein Eintrag, gegatet auf die Kachel.
    // Erledigung kommt aus den Lauf-Fertigmeldungen, nicht aus dem Monatsstatus.
    ...(deadlines.lohnschein_versand_1 ? [{ date: deadlines.lohnschein_versand_1, completed: lohnscheinCompleted?.lauf1, fieldName: 'lohnschein_versand_1_completed' }] : []),
    ...(deadlines.lohnschein_versand_2 ? [{ date: deadlines.lohnschein_versand_2, completed: lohnscheinCompleted?.lauf2, fieldName: 'lohnschein_versand_2_completed' }] : []),
    // OPPM QR-Code-Versand nur prüfen wenn am Standort konfiguriert.
    // Erledigung kommt aus customer_periodenfrist_status (periodenbezogen),
    // nicht mehr aus dem Monatsstatus des Arbeitsmonats.
    ...(hasOppmQrVersand(customer) ? [{ date: deadlines.oppm_qr_versand, completed: spezialfristCompleted?.oppmQr, fieldName: 'oppm_qr_versand_completed' }] : []),
    // OPPM Monatsübersicht QR-Code-Versand nur prüfen wenn am Standort konfiguriert
    ...(hasMonatsuebersichtQrVersand(customer) ? [{ date: deadlines.monatsuebersicht_qr_versand, completed: spezialfristCompleted?.monatsuebersichtQr, fieldName: 'monatsuebersicht_qr_versand_completed' }] : [])
  ];
  
  // NACHZÜGLER: offene Lohnschein-Läufe des zuletzt abgeschlossenen Monats
  if (nachzuegler && nachzuegler.length > 0) {
    nachzuegler.forEach(n => {
      deadlinesToCheck.push({
        date: n.faelligkeit,
        completed: false,
        fieldName: getNachzueglerKey(n)
      });
    });
  }

  // NACHZÜGLER SPEZIALFRISTEN: offene QR-Fristen des zuletzt abgeschlossenen Monats
  if (spezialfristNachzuegler && spezialfristNachzuegler.length > 0) {
    spezialfristNachzuegler.forEach(n => {
      deadlinesToCheck.push({
        date: n.faelligkeit,
        completed: false,
        fieldName: getSpezialfristNachzueglerKey(n)
      });
    });
  }

  // NACHZÜGLER INDIVIDUELLE FRISTEN: offene individuelle Fristen des zuletzt
  // abgeschlossenen Monats
  if (individualNachzuegler && individualNachzuegler.length > 0) {
    individualNachzuegler.forEach(n => {
      deadlinesToCheck.push({
        date: n.faelligkeit,
        completed: false,
        fieldName: getIndividualNachzueglerKey(n)
      });
    });
  }
  
  // Individuelle Deadlines hinzufügen
  if (individualDeadlines && individualDeadlines.length > 0) {
    individualDeadlines.forEach(individualDeadline => {
      const deadlineKey = `individual_${individualDeadline.id}`;
      const deadlineDate = deadlines[deadlineKey];
      const isCompleted = status?.individual_deadlines_status?.[individualDeadline.id] || false;
      
      deadlinesToCheck.push({
        date: deadlineDate,
        completed: isCompleted,
        fieldName: `individual_deadline_${individualDeadline.id}`
      });
    });
  }
  
  deadlinesToCheck.forEach(({ date, completed, fieldName }) => {
    if (!completed && date) {
      // WARTET AUF KUNDEN: ruhende Frist -> fest Priorität 2 (siehe Doku oben)
      if (ruhendeFristen.has(fieldName)) {
        highestPriority = Math.min(highestPriority, 2);
        return;
      }

      let effectiveDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

      // SNOOZE FEATURE: Wenn gesnoozed, nutze snoozed_until als effektives Datum
      if (isDeadlineSnoozed(fieldName, status) && status?.snoozed_deadlines?.[fieldName]) {
        const snoozedUntil = new Date(status.snoozed_deadlines[fieldName].snoozed_until + 'T00:00:00');
        // Nutze snoozed_until nur wenn es nach dem Original-Datum liegt
        if (snoozedUntil > effectiveDate) {
          effectiveDate = snoozedUntil;
        }
      }

      const daysDiff = Math.floor((effectiveDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
      
      let priority;
      if (daysDiff < 0) {
        priority = 0; // Überfällig - höchste Priorität
      } else if (daysDiff === 0) {
        priority = 1; // Heute fällig
      } else if (daysDiff === 1) {
        priority = 2; // Morgen fällig
      } else if (effectiveDate <= sundayThisWeek) {
        priority = 3; // Diese Woche fällig (bis Sonntag der aktuellen Woche)
      } else if (effectiveDate <= sundayNextWeek) {
        priority = 4; // Nächste Woche fällig (Montag bis Sonntag der nächsten Woche)
      } else {
        priority = 5; // Unter Kontrolle (alles danach)
      }
      
      // Nehme die höchste Priorität (niedrigste Zahl)
      if (priority < highestPriority) {
        highestPriority = priority;
      }
    }
  });
  
  return highestPriority;
};

/**
 * Hilfsfunktion: Erstellt ein benutzerfreundliches Label für eine individuelle Deadline
 */
export const getIndividualDeadlineLabel = (deadline: CustomerIndividualDeadline): string => {
  return deadline.title;
};

/**
 * Hilfsfunktion: Generiert einen eindeutigen Key für eine individuelle Deadline
 */
export const getIndividualDeadlineKey = (deadlineId: string): string => {
  return `individual_${deadlineId}`;
};
